// ../../helper/notebook-mentions.ts

// Use WORD JOINER as delimiter (invisible)
export const ZW = '\u2060'; // WORD JOINER

// Bits encoding chars (invisible)
const B0 = '\u200B'; // ZERO WIDTH SPACE
const B1 = '\u200C'; // ZERO WIDTH NON-JOINER

// ------------------------------------------------------------
// Encoding
// ------------------------------------------------------------

// Encode a JS string into UTF-8 bytes, then into invisible bits.
const encode = (s: string) => {
  const bytes = new TextEncoder().encode(s); // UTF-8
  let bits = '';
  for (const b of bytes) bits += b.toString(2).padStart(8, '0');
  return bits.replace(/0/g, B0).replace(/1/g, B1);
};

// Decode invisible bits -> UTF-8 string
const decode = (payload: string) => {
  const bits = payload.split(B0).join('0').split(B1).join('1');
  if (!/^[01]*$/.test(bits) || bits.length % 8 !== 0) return '';

  const bytes = new Uint8Array(bits.length / 8);
  for (let i = 0; i < bits.length; i += 8) {
    bytes[i / 8] = parseInt(bits.slice(i, i + 8), 2);
  }

  try {
    return new TextDecoder().decode(bytes);
  } catch {
    return '';
  }
};

// A hidden token is: ZW ZW + (B0/B1)+ + ZW ZW
// Make the regex STRICT so stripHidden doesn't eat arbitrary text.
const HIDDEN_PAYLOAD_RE = `[${B0}${B1}]+`;
const HIDDEN_TOKEN_RE = new RegExp(`${ZW}{2}(${HIDDEN_PAYLOAD_RE})${ZW}{2}`, 'g');

// Wrap encoded id with double delimiters
export const makeHidden = (userId: string) => `${ZW}${ZW}${encode(String(userId))}${ZW}${ZW}`;

// Remove all hidden ID tokens from text
export const stripHidden = (text: string) => text.replace(HIDDEN_TOKEN_RE, '');

// Extract unique mentioned userIds
export const extractMentionUserIds = (text: string) => {
  const ids = new Set<string>();
  let m: RegExpExecArray | null;

  HIDDEN_TOKEN_RE.lastIndex = 0;
  while ((m = HIDDEN_TOKEN_RE.exec(text)) !== null) {
    const decoded = decode(m[1] || '');
    if (decoded) ids.add(decoded);
  }
  return [...ids];
};

// ------------------------------------------------------------
// Mention range detection
// ------------------------------------------------------------

// Treat mention label chars (visible)
export const isMentionChar = (ch: string) => /[a-zA-Z0-9_-]/.test(ch);

// Fast finder: scan for "@label" + hidden token.
const REAL_MENTION_RE = new RegExp(
  // @ + label + hidden-token (strict payload)
  `@([a-zA-Z0-9_-]+)(${ZW}{2}${HIDDEN_PAYLOAD_RE}${ZW}{2})`,
  'g'
);

/**
 * Find mention range that covers a given index (raw indices).
 * This is used for atomic deletion.
 */
export const findMentionRangeAtIndex = (value: string, index: number) => {
  if (!value) return null;
  const i = Math.max(0, Math.min(index, value.length - 1));

  REAL_MENTION_RE.lastIndex = 0;
  let m: RegExpExecArray | null;

  while ((m = REAL_MENTION_RE.exec(value)) !== null) {
    const start = m.index; // '@' start
    const endHidden = start + m[0].length; // end right after hidden token
    let end = endHidden;

    // include one trailing space if present
    if (value[end] === ' ') end++;

    if (i >= start && i < end) return { start, end };
  }
  return null;
};

// ------------------------------------------------------------
// Rendering helper (linear time)
// ------------------------------------------------------------

export function splitByRealMentions(input: string) {
  if (!input) return [{ type: 'text' as const, value: '' }];

  const out: Array<
    { type: 'text'; value: string } | { type: 'mention'; label: string; raw: string }
  > = [];

  let last = 0;

  REAL_MENTION_RE.lastIndex = 0;
  let m: RegExpExecArray | null;

  while ((m = REAL_MENTION_RE.exec(input)) !== null) {
    const start = m.index ?? 0;
    const raw = m[0]; // includes @label + hidden token
    const label = m[1];

    if (start > last) {
      out.push({ type: 'text', value: stripHidden(input.slice(last, start)) });
    }

    // extend raw to include one trailing space if present
    let end = start + raw.length;
    if (input[end] === ' ') end++;

    out.push({ type: 'mention', label, raw: input.slice(start, end) });
    last = end;

    // IMPORTANT: because we manually extended end by a space, ensure regex continues correctly
    // by resetting lastIndex to last.
    REAL_MENTION_RE.lastIndex = last;
  }

  if (last < input.length) {
    out.push({ type: 'text', value: stripHidden(input.slice(last)) });
  }

  return out.length ? out : [{ type: 'text' as const, value: stripHidden(input) }];
}

export const extractMentionLabelsFromContent = (content: string) => {
  if (!content) return [];

  const labels = new Set<string>();
  const mentionRegex = /(^|\s)@([a-zA-Z0-9_-]+)/g;
  let match: RegExpExecArray | null;

  while ((match = mentionRegex.exec(content)) !== null) {
    const label = (match[2] || '').trim().toLowerCase();
    if (label) labels.add(label);
  }

  return [...labels];
};

export const resolveMentionedUserIdsFromContent = (content: string, teamMembers: any[]) => {
  if (!content) return [];

  const labelToIds = new Map<string, Set<number>>();
  teamMembers.forEach((member: any) => {
    const label = (member?.label || '').trim().toLowerCase();
    const id = Number(member?.userId ?? member?.id);
    if (!label || !Number.isInteger(id) || id <= 0) return;

    if (!labelToIds.has(label)) labelToIds.set(label, new Set<number>());
    labelToIds.get(label)?.add(id);
  });

  const mentionLabels = extractMentionLabelsFromContent(content);
  const out = new Set<number>();

  mentionLabels.forEach((label) => {
    const ids = labelToIds.get(label);
    if (!ids || ids.size !== 1) return;
    out.add(Array.from(ids)[0]);
  });

  return [...out];
};
